/**
 * Shared theme palette for the high-lift foil feature.
 *
 * One source of truth for every colour the foil canvas, control nets, gap-metric
 * overlay and classification chips draw with — so the whole feature follows
 * Thread's light/dark toggle (`data-theme` on <html>) instead of the old
 * hardcoded dark-only `COLORS` block in `FoilGeometryCanvas.tsx`.
 *
 * Two halves:
 *  - The `--v5-*` design tokens (bg/surface/line/ink/primary/blue/amber/warn …)
 *    are read live off `document.documentElement` with `getComputedStyle`, the
 *    same idiom as `TrustSurfacePanel.cssColor()`. They already carry both
 *    light and dark values from `thread-v5.css`.
 *  - The geometry/semantic colours (vane tan, control-net teal/purple, throat
 *    cyan, reattach pink, the actuator-disk + slipstream blues, the picker
 *    handle, the classification swatches) are NOT design tokens, so we define
 *    explicit light AND dark variants here. The DARK variants are lifted
 *    verbatim from the old `COLORS` object and `adaptive-sampling/theme.ts`
 *    so dark mode looks identical to today; the LIGHT variants are tuned to
 *    read on the light `--v5-bg` (#f7f7f4).
 *
 * Exports:
 *  - `useHighliftTheme()` — a hook that recomputes when `useTheme().theme`
 *    flips, so consumers re-render on toggle.
 *  - `readHighliftPalette(theme)` — a pure read for imperative/canvas code
 *    (e.g. inside a `requestAnimationFrame` draw loop) that can't use hooks.
 */

import { useMemo } from "react";

import { useTheme, type Theme } from "../../../context/ThemeContext";

export interface HighliftPalette {
  // ---- mapped from --v5-* design tokens (both light + dark defined in CSS) ----
  /** Page background (`--v5-bg`). */
  bg: string;
  /** Card / panel background (`--v5-surface`). */
  surface: string;
  /** Elevated panel background (`--v5-surface-2`). */
  surface2: string;
  /** Hairline border (`--v5-line`). */
  line: string;
  /** Stronger border (`--v5-line-strong`). */
  lineStrong: string;
  /** Primary text (`--v5-ink`). */
  ink: string;
  /** Secondary text (`--v5-ink-2`). */
  ink2: string;
  /** Muted text / axis labels (`--v5-ink-3`). */
  ink3: string;
  /** Faint text / disabled (`--v5-ink-4`). */
  ink4: string;
  /** Brand green (`--v5-primary`). */
  primary: string;
  /** Soft brand wash (`--v5-primary-soft`). */
  primarySoft: string;
  /** Text/icon colour to sit ON the brand primary (white reads on the green in both themes). */
  onPrimary: string;
  /** Accent blue (`--v5-blue`). */
  blue: string;
  /** Accent amber (`--v5-amber`). */
  amber: string;
  /** Warn red (`--v5-warn`). */
  warn: string;

  // ---- geometry colours (explicit per-theme; tuned to read on each bg) ----
  /** Main-element fill. */
  mainFace: string;
  /** Main-element outline. */
  mainEdge: string;
  /** Vane fill (tan). */
  vaneFace: string;
  /** Vane outline. */
  vaneEdge: string;
  /** Flaperon fill. */
  flapFace: string;
  /** Flaperon outline. */
  flapEdge: string;
  /** Leading-edge slat outline (proper slat element). */
  slatEdge: string;
  /** Krueger-flap outline. */
  kruegerEdge: string;
  /** Grid lines. */
  grid: string;
  /** Grid axis labels. */
  gridLabel: string;
  /** Actuator disk, unloaded. */
  diskOff: string;
  /** Actuator disk, loaded. */
  diskOn: string;
  /** Slipstream band fill. */
  slipstream: string;
  /** Deploy track (carriage path). */
  track: string;
  /** Deploy-hinge marker. */
  deployHinge: string;
  /** Flaperon-hinge marker. */
  flaperonHinge: string;
  /** Vane B-spline control net (teal). */
  vaneControl: string;
  /** Flaperon B-spline control net (purple). */
  flaperonControl: string;
  /** Slot-throat line (cyan). */
  throat: string;
  /** Reattachment marker (pink). */
  reattach: string;
  /** Draggable handle, idle. */
  handle: string;
  /** Draggable handle, hover/active. */
  handleHover: string;

  // ---- classification swatches (ok / low / conflict / extrap / no-data) ----
  /** ok / in-domain. */
  classOk: string;
  /** low confidence. */
  classLow: string;
  /** conflicting sources. */
  classConflict: string;
  /** extrapolation. */
  classExtrap: string;
  /** no data. */
  classNoData: string;

  /**
   * Perceptual ramp for Mach (or any [0,1] scalar). Returns an RGB triple in
   * 0–255 so callers can build `rgb()`/`rgba()` strings or feed three.js
   * `Color.setRGB` (divide by 255). Stops are chosen to read on the current
   * background.
   */
  machColor(t: number): [number, number, number];
}

/** The explicit (non-token) geometry + semantic colours, keyed on theme. */
interface GeometryColors {
  mainFace: string;
  mainEdge: string;
  vaneFace: string;
  vaneEdge: string;
  flapFace: string;
  flapEdge: string;
  slatEdge: string;
  kruegerEdge: string;
  grid: string;
  gridLabel: string;
  diskOff: string;
  diskOn: string;
  slipstream: string;
  track: string;
  deployHinge: string;
  flaperonHinge: string;
  vaneControl: string;
  flaperonControl: string;
  throat: string;
  reattach: string;
  handle: string;
  handleHover: string;
  classOk: string;
  classLow: string;
  classConflict: string;
  classExtrap: string;
  classNoData: string;
  /** Text/icon colour on the brand primary (white reads on the green in both themes). */
  onPrimary: string;
  /** Mach ramp stops (RGB triples, 0–255), low → high. */
  machStops: [number, number, number][];
}

/**
 * DARK variants — lifted verbatim from the previous `FoilGeometryCanvas`
 * `COLORS` block and `adaptive-sampling/theme.ts`, so dark mode is unchanged.
 */
const DARK_GEOMETRY: GeometryColors = {
  mainFace: "#cfd8e6",
  mainEdge: "#1f3a68",
  vaneFace: "#fbd4b4",
  vaneEdge: "#9c3d1a",
  flapFace: "#cfe9c8",
  flapEdge: "#2c6b2c",
  slatEdge: "#1f6f9c",
  kruegerEdge: "#8a5cc0",
  grid: "rgba(255,255,255,0.05)",
  gridLabel: "#8a8f98",
  diskOff: "rgba(138,147,163,0.35)",
  diskOn: "rgba(47,127,191,0.40)",
  slipstream: "rgba(47,127,191,0.10)",
  track: "#9c5bd1",
  deployHinge: "#9c5bd1",
  flaperonHinge: "#e08a1e",
  vaneControl: "#1b8a8a",
  flaperonControl: "#7a4fc0",
  throat: "#00d4ff",
  reattach: "#ff4080",
  handle: "#00d4ff",
  handleHover: "#7fe6ff",
  // ok = green, low = amber, conflict = pink/red, extrap = muted, no-data = faint
  classOk: "#2e9e5b",
  classLow: "#fdca26",
  classConflict: "#ff4080",
  classExtrap: "#8a8f98",
  classNoData: "#555a63",
  onPrimary: "#ffffff",
  // Plasma-ish ramp (lifted from SEQUENTIAL_PLASMA endpoints) — reads on #0a0a0a.
  machStops: [
    [13, 8, 135], // #0d0887 deep indigo (low Mach)
    [126, 3, 168], // #7e03a8
    [203, 70, 121], // #cb4679
    [248, 149, 64], // #f89540
    [240, 249, 33], // #f0f921 yellow (high Mach)
  ],
};

/**
 * LIGHT variants — tuned to read on the light `--v5-bg` (#f7f7f4). Faces are
 * pushed a touch deeper and edges darkened for contrast; the translucent
 * disk/slipstream/grid switch to black-based alphas; throat/reattach/control
 * accents keep their hue but go darker so they don't wash out.
 */
const LIGHT_GEOMETRY: GeometryColors = {
  mainFace: "#aebfd6",
  mainEdge: "#1f3a68",
  vaneFace: "#f2b98c",
  vaneEdge: "#8a3414",
  flapFace: "#aedaa3",
  flapEdge: "#27602a",
  slatEdge: "#175a80",
  kruegerEdge: "#6e3fa8",
  grid: "rgba(23,25,22,0.07)",
  gridLabel: "#6f766d",
  diskOff: "rgba(111,118,109,0.30)",
  diskOn: "rgba(45,110,150,0.32)",
  slipstream: "rgba(45,110,150,0.12)",
  track: "#7b3fb0",
  deployHinge: "#7b3fb0",
  flaperonHinge: "#b06410",
  vaneControl: "#0f6d6d",
  flaperonControl: "#5e35a8",
  throat: "#0090b8",
  reattach: "#d4296a",
  handle: "#0090b8",
  handleHover: "#0073a0",
  classOk: "#1f7a45",
  classLow: "#a77a20",
  classConflict: "#c2412a",
  classExtrap: "#6f766d",
  classNoData: "#9aa197",
  onPrimary: "#ffffff",
  // Same plasma hues, but the very-light yellow endpoint is darkened so it
  // stays visible against a near-white background.
  machStops: [
    [13, 8, 135], // deep indigo (low Mach)
    [126, 3, 168],
    [203, 70, 121],
    [222, 120, 40], // slightly deeper orange
    [196, 168, 0], // darkened yellow so it reads on light bg
  ],
};

const TOKEN_FALLBACK_LIGHT = {
  bg: "#f7f7f4",
  surface: "#fffffc",
  surface2: "#f0f1ec",
  line: "#dedfd8",
  lineStrong: "#c7cabf",
  ink: "#171916",
  ink2: "#3c413b",
  ink3: "#6f766d",
  ink4: "#9aa197",
  primary: "#006d3c",
  primarySoft: "#e4f1e9",
  blue: "#39738f",
  amber: "#a77a20",
  warn: "#b74b44",
} as const;

const TOKEN_FALLBACK_DARK = {
  bg: "#0b0f0e",
  surface: "#111614",
  surface2: "#171f1c",
  line: "#2b3430",
  lineStrong: "#3c4943",
  ink: "#edf1eb",
  ink2: "#cfd8d0",
  ink3: "#9aa79d",
  ink4: "#68746d",
  primary: "#6fd0a2",
  primarySoft: "#143326",
  blue: "#70b5d2",
  amber: "#d8a542",
  warn: "#df756d",
} as const;

type TokenKey = keyof typeof TOKEN_FALLBACK_LIGHT;

/**
 * Resolve a `--v5-*` token from the theme-keyed baked tables (FXC-9009).
 *
 * We deliberately do NOT read the live CSS var with getComputedStyle. Two reasons it was wrong:
 *  1. SCOPE — the dark token block in thread-v5.css is `[data-theme="dark"] .v5-app` (not `:root`),
 *     so a read off `document.documentElement` always returns the LIGHT `:root` values.
 *  2. RACE — this hook reads at RENDER time (memoised on `theme`), but the `data-theme` attribute is
 *     committed in a later effect, so even reading the right element returned the pre-toggle value
 *     and then cached it (every token-derived colour — plot/3D backgrounds, surfaces — stayed light
 *     in dark mode).
 * The baked tables below are verbatim copies of the CSS token values, so keying straight off `theme`
 * (the reliable React state) is correct + race-free + SSR-safe for both themes.
 */
function readToken(key: TokenKey, theme: Theme): string {
  return (theme === "dark" ? TOKEN_FALLBACK_DARK : TOKEN_FALLBACK_LIGHT)[key];
}

/** Build the Mach ramp from a theme's stops (piecewise-linear in RGB). */
function makeMachColor(stops: [number, number, number][]): HighliftPalette["machColor"] {
  return (t: number): [number, number, number] => {
    const clamped = Number.isFinite(t) ? Math.max(0, Math.min(1, t)) : 0;
    const n = stops.length;
    if (n === 0) return [0, 0, 0];
    if (n === 1) return [...stops[0]!] as [number, number, number];
    const scaled = clamped * (n - 1);
    const i = Math.min(n - 2, Math.floor(scaled));
    const f = scaled - i;
    const a = stops[i]!;
    const b = stops[i + 1]!;
    return [
      Math.round(a[0] + (b[0] - a[0]) * f),
      Math.round(a[1] + (b[1] - a[1]) * f),
      Math.round(a[2] + (b[2] - a[2]) * f),
    ];
  };
}

/**
 * Pure palette read — for imperative/canvas code that can't call hooks. Reads
 * the live `--v5-*` tokens off <html> and pairs them with the per-theme
 * geometry/semantic colours.
 */
export function readHighliftPalette(theme: Theme): HighliftPalette {
  const geo = theme === "dark" ? DARK_GEOMETRY : LIGHT_GEOMETRY;
  return {
    bg: readToken("bg", theme),
    surface: readToken("surface", theme),
    surface2: readToken("surface2", theme),
    line: readToken("line", theme),
    lineStrong: readToken("lineStrong", theme),
    ink: readToken("ink", theme),
    ink2: readToken("ink2", theme),
    ink3: readToken("ink3", theme),
    ink4: readToken("ink4", theme),
    primary: readToken("primary", theme),
    primarySoft: readToken("primarySoft", theme),
    blue: readToken("blue", theme),
    amber: readToken("amber", theme),
    warn: readToken("warn", theme),
    mainFace: geo.mainFace,
    mainEdge: geo.mainEdge,
    vaneFace: geo.vaneFace,
    vaneEdge: geo.vaneEdge,
    flapFace: geo.flapFace,
    flapEdge: geo.flapEdge,
    slatEdge: geo.slatEdge,
    kruegerEdge: geo.kruegerEdge,
    grid: geo.grid,
    gridLabel: geo.gridLabel,
    diskOff: geo.diskOff,
    diskOn: geo.diskOn,
    slipstream: geo.slipstream,
    track: geo.track,
    deployHinge: geo.deployHinge,
    flaperonHinge: geo.flaperonHinge,
    vaneControl: geo.vaneControl,
    flaperonControl: geo.flaperonControl,
    throat: geo.throat,
    reattach: geo.reattach,
    handle: geo.handle,
    handleHover: geo.handleHover,
    classOk: geo.classOk,
    classLow: geo.classLow,
    classConflict: geo.classConflict,
    classExtrap: geo.classExtrap,
    classNoData: geo.classNoData,
    onPrimary: geo.onPrimary,
    machColor: makeMachColor(geo.machStops),
  };
}

/**
 * Theme-aware palette hook. Recomputes (and re-renders the consumer) whenever
 * `useTheme().theme` flips, so canvas/SVG colours follow the global toggle.
 */
export function useHighliftTheme(): HighliftPalette {
  const { theme } = useTheme();
  return useMemo(() => readHighliftPalette(theme), [theme]);
}
